/**
 * A pult beolvasó ablaka (#168, Karlo terve 2026-09-16, 2. és 3. képernyő).
 *
 * MIÉRT KÜLÖN FÁJL. A `CheckIn.tsx` a beolvasás bevezetésével 1152 sorra és 32
 * kognitív komplexitásra nőtt — a szolgáltatás határa 15, a szolgálati
 * `CLAUDE.md` pedig 150 sorban húzza meg a komponens méretét. Az ablak az
 * egyetlen darab, ami önállóan is értelmes: nem kérdez, nem ír, csak megmutat
 * egy jegyet és egyetlen műveletet kínál.
 *
 * MIT NEM TUD, SZÁNDÉKOSAN. Nem tud a hálózatról, a beléptetésről és a
 * nyomtatásról. A címke tartalmát is a HÍVÓ adja (`badge`), nem maga állítja
 * elő: így az ablakban látott előnézet és a papírra kerülő nyomat ugyanabból
 * az egy forrásból származik. Ha az ablak maga rakná össze, két forrás lenne
 * ugyanarra a címkére, és a kezelő jóváhagyhatna valamit, ami nem az, ami
 * kijön a nyomtatóból.
 */

import {
  CloseCircleOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LoginOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  SaveOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import { Alert, Button, Input, Modal, Row, Space, Spin, Tag, Typography } from 'antd';
import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BADGE_HEIGHT_MM, BADGE_WIDTH_MM, type BadgeContent } from '../config/badgePrint';
import { fieldChanged, hasChanges, type ScanEdits, scanEditChanges } from '../config/scanEdits';
import BadgePreview from './BadgePreview';
import './ScanResultModal.css';

const { Text } = Typography;

/** Which desk action is running in the window, if any. */
export type ScanAction = 'print' | 'save';

/** Amit az ablak a jegyről ténylegesen olvas. */
export interface ScanModalTicket {
  ticketNumber: string;
  firstName?: string;
  lastName?: string;
  checkedIn: boolean;
  checkedInAt?: string;
  checkedInBy?: string | null;
  /** NAGY T-vel, ahogy a `GET /tickets` adja — lásd a `CheckIn.tsx` `Ticket` típusát. */
  TicketType?: { name: string };
}

export type ScanModalState<T> =
  /** Required, so every opener names itself; `'row'` is the desk list's pencil, where no QR was read. */
  | { kind: 'ticket'; ticket: T; via: 'scan' | 'row' }
  | { kind: 'other-event'; code: string }
  | { kind: 'not-found'; code: string }
  /**
   * Beolvasva, de a jegylista még nem áll készen (#168).
   *
   * Az ablak AZONNAL kinyílik erre, és a lista megérkezésekor a hívó cseréli
   * le a végleges állapotra. Enélkül a várakozás néma lenne: a kezelő beolvas,
   * nem történik semmi, és újra beolvas — a pultnál ez a leggyakoribb módja
   * annak, hogy egy lassú betöltés elromlott olvasónak látsszon.
   */
  | { kind: 'pending'; code: string }
  /** A jegylista betöltése ELBUKOTT — erre a kódra most nem tudunk válaszolni. */
  | { kind: 'list-failed'; code: string };

interface ScanResultModalProps<T extends ScanModalTicket> {
  state: ScanModalState<T> | null;
  /**
   * A címke tartalma — EGY forrás az előnézetnek és a nyomatnak.
   *
   * A JAVÍTOTT értékeket kapja, nem a jegyet nyersen: így az előnézet a
   * gépelés közben frissül, de az összeállítás továbbra is a hívónál marad.
   */
  badge: (ticket: T, edits: ScanEdits) => BadgeContent;
  /**
   * What the badge shows for the ticket before any correction. It comes from the caller, which also
   * saves and prints, so the fields start from, and Save compares against, the same values.
   */
  original: (ticket: T) => ScanEdits;
  /** A kategória felirata a szótárból; ismeretlen értékre üres. */
  typeLabel: (ticket: T) => string;
  /** Az érkezési idő a felület nyelvén. */
  formatTime: (iso: string) => string;
  busy: ScanAction | null;
  /**
   * Whether this event's desk prints badges (#202). Off, the window shows the
   * details and a plain "Check in": no badge preview, and a guest who is
   * already in gets no primary action, because there is nothing to reprint.
   */
  printEnabled: boolean;
  onClose: () => void;
  /** The primary action: print and check in, or only check in when `printEnabled` is off. */
  onPrint: (ticket: T, edits: ScanEdits) => void;
  /** Saves the correction only; the caller closes the window when it succeeds. */
  onSave: (ticket: T, edits: ScanEdits) => void;
  /**
   * Egy ÚJ beolvasás, ami a nyitott ablakba érkezett (#168, 2026-09-18).
   *
   * A hívó ugyanazon az úton dönt róla, mint a keresőmezőbe olvasott kódról —
   * a kezelő a következő vendéget olvasta be, nem az előzőt akarja nyomtatni.
   */
  onScan: (code: string) => void;
  /**
   * Hol kezdődik a jelen esemény jegykódja a begépelt szövegben (-1: sehol).
   * A lassan küldő olvasó felismeréséhez — lásd `usePrimaryOnEnter`.
   */
  codeStart: (typed: string) => number;
}

function ScanResultModal<T extends ScanModalTicket>({
  state,
  badge,
  original,
  typeLabel,
  formatTime,
  busy,
  printEnabled,
  onClose,
  onPrint,
  onSave,
  onScan,
  codeStart,
}: ScanResultModalProps<T>): React.ReactElement {
  const { t } = useTranslation();

  /*
    NYITVA VAN-E MÉG A JEGY — a bezárás UTÁNI Enter ellen.

    Bezáráskor az antd a záró animáció végéig bent hagyja a tartalmat, és NEM
    is frissíti: a `TicketBody` a régi állapotával él tovább, a figyelője is.
    Mérve: Esc után az animáció alatt ütött Enter még kinyomtatta a már
    bezárt jegy címkéjét. Ez a ref a SZÜLŐ commitjában frissül, ami a bezárás
    pillanatában is lefut, ezért a figyelő ebből tudja, hogy már nem él.
    Effektben, nem renderben: egy eldobott render nem hagyhat benne olyan
    értéket, ami sosem került ki a képernyőre.
  */
  const openRef = useRef(false);
  const isOpenTicket = state?.kind === 'ticket';
  useLayoutEffect(() => {
    openRef.current = isOpenTicket;
  }, [isOpenTicket]);

  // A piros ikon RIASZTÁST jelent, nem „nem jegy"-et: a várakozó ablak a
  // semleges QR-ikont kapja, mert ott még nem történt semmi rossz.
  const alarm =
    state !== null &&
    state.kind !== 'pending' &&
    (state.kind !== 'ticket' || state.ticket.checkedIn);

  const title = (
    <div className="scan-modal__title">
      <span className={`scan-modal__icon${iconVariant(state, alarm)}`}>
        {iconFor(state, alarm)}
      </span>
      <div>
        <div className="scan-modal__heading">{t(titleKeyFor(state))}</div>
        {state?.kind === 'ticket' && (
          <div className="scan-modal__sub">
            {t(state.via === 'row' ? 'checkin.rowSubtitle' : 'checkin.scanSubtitle', {
              number: state.ticket.ticketNumber,
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={state !== null}
      onCancel={onClose}
      // Az ablak élő matrica-előnézetet tartalmaz, ami egy iframe. Bezárás
      // után bent hagyva a következő beolvasásig az ELŐZŐ résztvevő címkéje
      // maradna felépítve a memóriában.
      destroyOnHidden
      // The caller puts focus in the search box; antd's restore would move it back to the row
      // pencil, and the next reader scan's Enter would reopen the previous guest.
      focusTriggerAfterClose={false}
      footer={null}
      width={600}
      className="scan-modal"
      title={title}
    >
      {state?.kind === 'ticket' ? (
        <TicketBody
          // A JEGYSZÁM a kulcs: két beolvasás között az ablak nyitva
          // maradhat, és kulcs nélkül az ELŐZŐ résztvevő félig átírt neve
          // maradna a mezőkben a következőnél.
          key={state.ticket.ticketNumber}
          ticket={state.ticket}
          badge={(edits) => badge(state.ticket, edits)}
          original={original(state.ticket)}
          typeLabel={typeLabel(state.ticket)}
          formatTime={formatTime}
          busy={busy}
          printEnabled={printEnabled}
          openRef={openRef}
          onClose={onClose}
          onPrint={(edits) => onPrint(state.ticket, edits)}
          onSave={(edits) => onSave(state.ticket, edits)}
          onScan={onScan}
          codeStart={codeStart}
        />
      ) : (
        <NonTicketBody state={state} onClose={onClose} />
      )}
    </Modal>
  );
}

/**
 * Az ablak címének szótárkulcsa.
 *
 * Külön függvény, nem beágyazott feltételsor: öt ág van, és a `TicketBody`
 * kiemelésének éppen az volt az oka, hogy ez a fájl átlépte a kognitív
 * komplexitás határát. A visszatérési típus szándékosan nincs `string`-re
 * tágítva — így a fordító látja, hogy mind az öt kulcs létezik a szótárban.
 */
function titleKeyFor(state: ScanModalState<ScanModalTicket> | null) {
  if (state?.kind === 'ticket') {
    return state.ticket.checkedIn ? 'checkin.scanAlreadyTitle' : 'checkin.scanFoundTitle';
  }
  if (state?.kind === 'pending') return 'checkin.scanPendingTitle';
  if (state?.kind === 'list-failed') return 'checkin.scanListFailedTitle';
  return 'checkin.scanNotFoundTitle';
}

/**
 * A fejléc ikonjának színe: zöld a megtalált jegyen, piros a riasztáson,
 * semleges a várakozón. A terv ugyanezt a zöldet használja a „kész" jelzésre.
 */
function iconVariant(state: ScanModalState<ScanModalTicket> | null, alarm: boolean): string {
  if (alarm) return ' scan-modal__icon--alarm';
  return state?.kind === 'ticket' ? ' scan-modal__icon--found' : '';
}

function iconFor(state: ScanModalState<ScanModalTicket> | null, alarm: boolean): React.ReactNode {
  if (alarm) return <CloseCircleOutlined />;
  return state?.kind === 'ticket' ? <ScanOutlined /> : <QrcodeOutlined />;
}

/** The primary button's label. A desk without a printer never shows it on a guest who is in. */
function primaryLabelKey(printEnabled: boolean, checkedIn: boolean) {
  if (!printEnabled) return 'checkin.checkInOnly';
  return checkedIn ? 'checkin.reprintOnly' : 'checkin.printAndCheckIn';
}

/** A magyarázó szöveg kulcsa a három nem-várakozó, nem-jegy ághoz. */
function descriptionKeyFor(state: ScanModalState<ScanModalTicket> | null) {
  if (state?.kind === 'other-event') return 'checkin.scanOtherEventBody';
  if (state?.kind === 'list-failed') return 'checkin.scanListFailedBody';
  return 'checkin.scanNotFoundBody';
}

interface NonTicketBodyProps {
  state: Exclude<ScanModalState<ScanModalTicket>, { kind: 'ticket' }> | null;
  onClose: () => void;
}

/**
 * Minden ág, ami NEM egy megtalált jegy.
 *
 * A várakozó ág külön áll a másik háromtól, mert ott nincs mit magyarázni:
 * a kezelő a beolvasott kódot látja és azt, hogy dolgozunk rajta. A bezárás
 * mindhárom ágon ott van — a várakozót is meg kell tudni szakítani, különben
 * egy rossz beolvasás után várni kell a lista végéig.
 */
function NonTicketBody({ state, onClose }: NonTicketBodyProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <Space direction="vertical" size="middle" className="scan-modal__body">
      {state?.kind === 'pending' ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }} align="center">
          <Spin />
          <Text strong>{state.code}</Text>
          <Text type="secondary">{t('checkin.scanPendingBody')}</Text>
        </Space>
      ) : (
        <Alert
          type={state?.kind === 'list-failed' ? 'error' : 'warning'}
          showIcon
          message={state?.code}
          description={t(descriptionKeyFor(state))}
        />
      )}
      <Row justify="end">
        <Button type="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </Row>
    </Space>
  );
}

interface TicketBodyProps {
  ticket: ScanModalTicket;
  badge: (edits: ScanEdits) => BadgeContent;
  original: ScanEdits;
  typeLabel: string;
  formatTime: (iso: string) => string;
  busy: ScanAction | null;
  printEnabled: boolean;
  /** Igaz, amíg ez a jegy nyitva van; a záró animáció alatt már hamis. */
  openRef: React.RefObject<boolean>;
  onClose: () => void;
  onPrint: (edits: ScanEdits) => void;
  onSave: (edits: ScanEdits) => void;
  onScan: (code: string) => void;
  codeStart: (typed: string) => number;
}

function TicketBody({
  ticket,
  badge,
  original,
  typeLabel,
  formatTime,
  busy,
  printEnabled,
  openRef,
  onClose,
  onPrint,
  onSave,
  onScan,
  codeStart,
}: TicketBodyProps): React.ReactElement {
  const { t } = useTranslation();

  /*
    A JAVÍTÁS ÁLLAPOTA — a jegy értékeiből indulva.

    A kezdőérték a feloldott cégnév, NEM a nyers `organizationName`: a kezelő
    azt látja a mezőben, ami ma a címkére menne, és azt javítja. Ha a nyers
    mezőt mutatnánk, a ma is helyes cégnévvel rendelkező jegyeken üres mező
    fogadná — és a kezelő azt hinné, hogy nincs cég.
  */
  const [edits, setEdits] = useState<ScanEdits>(original);

  const setField = (field: keyof ScanEdits) => (value: string) =>
    setEdits((prev) => ({ ...prev, [field]: value }));

  /*
    EGY INDÍTÁS = EGY CÍMKE (Karlo §2, 2026-09-18).

    A gomb és az Enter UGYANEZT hívja, és a folyamatban lévő nyomtatás alatt
    egyik sem indít újat. A `busy` a hívótól jön, és elég: a hívó a
    nyomtatás ELSŐ lépésében beállítja, a React pedig a billentyű- és a
    kattintásesemény végén szinkron renderel, tehát a következő leütés már a
    friss értéket látja. (Egy külön „már elindult" jelzőt mértem, és a
    próbákban semmit nem változtatott — ezért nincs.)
  */
  // Without a printer there is nothing to do for a guest who is already in: no reprint, and no
  // second check-in. Enter must not reach the caller either.
  const hasPrimary = printEnabled || !ticket.checkedIn;

  const firePrimary = () => {
    // Also while a save runs: it would otherwise be followed by a second PATCH of the same correction.
    if (busy !== null || !hasPrimary) return;
    onPrint(edits);
  };

  const canSave = busy === null && hasChanges(scanEditChanges(original, edits));

  const rootRef = useRef<HTMLDivElement>(null);
  usePrimaryOnEnter(rootRef, openRef, {
    fire: firePrimary,
    scan: onScan,
    codeStart,
    edits,
    restore: setEdits,
  });

  return (
    <div ref={rootRef}>
      <div className="scan-modal__body">
        {ticket.checkedIn && (
          /*
          A 3. képernyő piros figyelmeztetése, Karlo terve szerint az érkezés
          idejével ÉS a beléptetővel („pult: Karlo R.").

          KÉT FELIRAT, mert a név hiányozhat, és a hiánya értelmes:
          - a #168 előtt beléptetett jegyeken nincs, mert akkor még nem
            rögzítettük — ezekre visszamenőleg nevet kitalálni hazugság lenne;
          - a NÉVTELEN ajtón átjött résztvevőn sincs.
          Ilyenkor a rövidebb felirat megy ki. Egy „(—)" zárójel a piros
          figyelmeztetésen úgy nézne ki, mintha hiányozna valami, amit tudnunk
          kellene.
        */
          <Alert
            type="error"
            showIcon
            message={
              ticket.checkedInBy
                ? t('checkin.scanAlreadyAtBy', {
                    time: ticket.checkedInAt ? formatTime(ticket.checkedInAt) : '—',
                    who: ticket.checkedInBy,
                  })
                : t('checkin.scanAlreadyAt', {
                    time: ticket.checkedInAt ? formatTime(ticket.checkedInAt) : '—',
                  })
            }
            // The printing text offers a reprint, which a desk without a printer does not have.
            description={t(
              printEnabled ? 'checkin.scanAlreadyBody' : 'checkin.scanAlreadyBodyNoPrint'
            )}
          />
        )}

        {/*
          A terv szerint a cég a SAJÁT sorában áll, a két névmező alatta,
          egymás mellett. A név nagy, talpas betűvel: a pultos azt ellenőrzi,
          ami a címkére kerül, és a név a címke legfontosabb sora.
        */}
        <div className="scan-modal__fields">
          <EditableField
            label={t('checkin.columnCompany')}
            optional={t('checkin.scanCompanyOptional')}
            value={edits.company}
            original={original.company}
            onChange={setField('company')}
          />
          <div className="scan-modal__names">
            <EditableField
              label={t('checkin.scanLastName')}
              value={edits.lastName}
              original={original.lastName}
              onChange={setField('lastName')}
              display
            />
            <EditableField
              label={t('checkin.scanFirstName')}
              value={edits.firstName}
              original={original.firstName}
              onChange={setField('firstName')}
              display
            />
          </div>
        </div>

        <div className="scan-modal__meta">
          <div className="scan-meta">
            <span className="scan-meta__label">{t('checkin.scanAttendeeType')}</span>
            {typeLabel ? <Tag>{typeLabel}</Tag> : <Text type="secondary">—</Text>}
          </div>
          <div className="scan-meta">
            <span className="scan-meta__label">{t('checkin.columnTicketType')}</span>
            <span className="scan-meta__value">{ticket.TicketType?.name || '—'}</span>
          </div>
          <div className="scan-meta">
            <span className="scan-meta__label">{t('common.status')}</span>
            {ticket.checkedIn ? (
              <Tag color="success">{t('checkin.checkedIn')}</Tag>
            ) : (
              <Tag>{t('checkin.notCheckedIn')}</Tag>
            )}
          </div>
        </div>

        {printEnabled ? (
          <div className="scan-preview">
            <div className="scan-preview__head">
              <span className="scan-preview__title">{t('checkin.badgePreview')}</span>
              <span className="scan-preview__size">
                {BADGE_WIDTH_MM} × {BADGE_HEIGHT_MM} mm
              </span>
            </div>
            <BadgePreview content={badge(edits)} />
            <span className="scan-preview__hint">
              <InfoCircleOutlined aria-hidden />
              {t('checkin.scanEditHint')}
            </span>
          </div>
        ) : (
          <span className="scan-preview__hint scan-preview__hint--plain">
            <InfoCircleOutlined aria-hidden />
            {t('checkin.scanEditHintNoPrint')}
          </span>
        )}
      </div>

      <div className="scan-modal__footer">
        <Button onClick={onClose}>
          {t('common.cancel')}
          {/* A gyorsbillentyű csak látvány: a gomb neve „Mégse" marad. */}
          <span className="scan-kbd scan-kbd--plain" aria-hidden>
            (Esc)
          </span>
        </Button>
        {/* Deliberately not wired to Enter: the desk's Enter prints, and that habit must not change. */}
        <Button
          icon={<SaveOutlined />}
          loading={busy === 'save'}
          disabled={!canSave}
          onClick={() => {
            if (canSave) onSave(edits);
          }}
        >
          {t('common.save')}
        </Button>
        {/*
          EGYETLEN elsődleges gomb, ahogy a terv kéri, és a felirata az
          állapottól függ. Már bent lévő jegyen „Csak újranyomtatás": nincs
          második beléptetés, az első érkezési idő marad. A művelet mindkét
          esetben ugyanaz — a hívó őrei intézik a különbséget. Nyomtató nélkül
          (#202) a felirat „Beléptetés", és bent lévő jegyen nincs gomb.
        */}
        {hasPrimary && (
          <Button
            type="primary"
            icon={printEnabled ? <PrinterOutlined /> : <LoginOutlined />}
            loading={busy === 'print'}
            disabled={busy === 'save'}
            onClick={firePrimary}
          >
            {t(primaryLabelKey(printEnabled, ticket.checkedIn))}
            <span className="scan-kbd" aria-hidden>
              Enter
            </span>
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Az olvasó felismerése: ennyi karakter, egyenként legfeljebb ennyi idő alatt.
 *
 * A kézi olvasó billentyűzetként gépel, de gépi tempóban — karakterenként
 * néhány ezredmásodperc, és a kód végén Enter. A pult kódja 30 karakter
 * fölött van. Ember ilyen tempóban nyolc leütést egymás után nem ír: a gyors
 * gépelés is 60–100 ms körül van leütésenként.
 */
const SCAN_GAP_MS = 35;
const SCAN_MIN_CHARS = 8;

/** Egy ÚJ Enter-leütés: nem ismétlés, nem IME-lezárás, nem kezelte már más. */
function isFreshEnter(event: KeyboardEvent): boolean {
  return event.key === 'Enter' && !event.repeat && !event.isComposing && !event.defaultPrevented;
}

/** Egy begépelt karakter — nem vezérlőbillentyű, nem gyorsbillentyű. */
function isTypedChar(event: KeyboardEvent): boolean {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}

/** A leütés a párbeszédablakon belül indult, vagy a fókusz nélküli lapon. */
function startsInDialog(target: EventTarget | null, dialog: Element | null | undefined): boolean {
  if (!(dialog && target instanceof Node)) return false;
  return dialog.contains(target) || target.contains(dialog);
}

/** Gombon ütött Enter: ott a böngésző saját kattintása a művelet. */
function onButton(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('button, a[href], [role="button"]') !== null;
}

/**
 * Az utolsó Enter óta a párbeszédablakban leütött karakterek.
 *
 * Minden karakterhez eltesszük a javítás állapotát a leütése ELŐTT: így a
 * kód akárhol kezdődik a sorozatban, pontosan a kód előtti állapot áll
 * vissza, és a pultos korábbi, saját leütései megmaradnak.
 */
interface Run {
  text: string;
  befores: ScanEdits[];
  last: number;
  /** A jelenlegi gépi tempójú szakasz első karakterének indexe. */
  fastFrom: number;
}

/** Egy felismert beolvasás: a kód, és a javítás állapota az első karaktere előtt. */
interface Scanned {
  code: string;
  before: ScanEdits;
}

interface PrimaryHandlers {
  /** A fő művelet. */
  fire: () => void;
  /** Egy új beolvasás a hívónak. */
  scan: (code: string) => void;
  /** A jelen esemény jegykódjának kezdete a szövegben, vagy -1. */
  codeStart: (typed: string) => number;
  /** A javítás mostani állapota. */
  edits: ScanEdits;
  /** A javítás visszaállítása — az olvasó a fókuszált mezőbe is gépelhetett. */
  restore: (edits: ScanEdits) => void;
}

/**
 * Az Enter a fő műveletet indítja (Karlo §2: „There is a single primary
 * action, Nyomtatás és beléptetés, also triggered by Enter").
 *
 * MIÉRT A DOKUMENTUMON FIGYEL, ÉS NEM A TÖRZSÖN. Nyitáskor az antd a fókuszt
 * a párbeszédablak egy őrelemére teszi, ami a tartalmat KÖRÜLÖLELI — a
 * törzsre tett `onKeyDown` így nem kapná meg az Entert. Ezért a figyelő a
 * dokumentumon ül, és csak a SAJÁT párbeszédablakán belül (vagy a fókusz
 * nélküli lapon) induló leütésre válaszol; a háttérben maradt keresőmezőre
 * nem. Az első beolvasás Entere ezért nem nyomtat: az a keresőmezőben
 * születik (valódi Chrome-ban mérve).
 *
 * A NYITOTT ABLAKBA ÉRKEZŐ BEOLVASÁS a következő vendég, nem nyomtatási
 * parancs (Norbert döntése, 2026-09-18). Mérve, e nélkül: a nyitott ablaknál
 * beolvasott következő vendég kinyomtatta az ELŐZŐ címkéjét, és ha a cégmező
 * volt fókuszban, a kód a cégnév mögé íródott, és azzal ment ki. Ezért a
 * gépi tempójú sorozatot lezáró Enter nem nyomtat: a mezőt visszaállítja,
 * és a kódot a hívóhoz küldi, ahogy a keresőmező tenné. Ilyenkor a gombon
 * ütött Enter saját kattintását is elnyeli — különben egy fókuszált fő
 * gombon a böngésző maga nyomtatna.
 *
 * MIT NEM INDÍT, SZÁNDÉKOSAN:
 * - **A lenyomva tartott Enter ismétlését** (`repeat`): egy Enter = egy címke.
 * - **A gombon ütött (emberi) Entert.** Egy fókuszált gombon az Enter a
 *   böngésző saját kattintása: a „Mégse" gombon bezár, a fő gombon a
 *   kattintás maga indít. Ha itt is indítanánk, a Mégse nyomtatna.
 * - **Az ékezetes bevitel (IME) közbeni Entert**, ami a szó lezárása.
 *
 * A szerkesztőmezőben kézzel ütött Enter IS indít, a beírt javítással: a
 * mező minden leütést azonnal az állapotba ír, tehát a félig begépelt érték
 * sem vész el.
 */
function usePrimaryOnEnter(
  rootRef: React.RefObject<HTMLDivElement | null>,
  openRef: React.RefObject<boolean>,
  handlers: PrimaryHandlers
): void {
  // A legutóbbi commit kezelői: a figyelő egyszer kerül fel, de mindig a
  // FRISS javítással kell dolgoznia.
  const handlersRef = useRef(handlers);
  useLayoutEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    let run: Run | null = null;

    const track = (event: KeyboardEvent) => {
      const current: Run = run ?? { text: '', befores: [], last: -1, fastFrom: 0 };
      // A gépi szakasz megszakad, ha a leütés lassú volt — vagy ha egy
      // lenyomva tartott billentyű ismétlése, ami ember, nem olvasó.
      if (current.last < 0 || event.repeat || event.timeStamp - current.last > SCAN_GAP_MS) {
        current.fastFrom = current.text.length;
      }
      current.befores.push(handlersRef.current.edits);
      current.text += event.key;
      current.last = event.timeStamp;
      run = current;
    };

    /**
     * A lezárt sorozatból felismert beolvasás, vagy `null`. Két jel, bármelyik
     * elég: a gépi tempó (legalább SCAN_MIN_CHARS karakter, és rögtön utána
     * az Enter), vagy a tartalom (a jelen esemény jegykódja).
     */
    const takeScan = (event: KeyboardEvent): Scanned | null => {
      const done = run;
      run = null;
      if (!done) return null;
      const fast = done.text.length - done.fastFrom >= SCAN_MIN_CHARS;
      const byTempo = fast && event.timeStamp - done.last <= SCAN_GAP_MS ? done.fastFrom : -1;
      const byContent = handlersRef.current.codeStart(done.text);
      // Ha mindkét jel felel, a KORÁBBI kezdet nyer. A kód közben egyszer
      // megakadó olvasó kettévágja a gépi szakaszt: a tempó ekkor csak a
      // szünet utáni részt látja — a puszta jegyszámot, ami a vendéget még
      // megtalálja —, és a mezőt az esemény-azonosító UTÁNI állapotra állítja
      // vissza, így az előtag a cégnévben marad. Terhelt CI-gépen mérve, ahol
      // a leütések közti renderelés volt ez a szünet.
      const at = byContent >= 0 && (byTempo < 0 || byContent < byTempo) ? byContent : byTempo;
      return at < 0 ? null : { code: done.text.slice(at), before: done.befores[at] };
    };

    const onEnter = (event: KeyboardEvent) => {
      const scanned = takeScan(event);
      if (scanned) {
        event.preventDefault();
        handlersRef.current.restore(scanned.before);
        handlersRef.current.scan(scanned.code);
        return;
      }
      if (onButton(event.target)) return;
      event.preventDefault();
      handlersRef.current.fire();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!openRef.current) return;
      if (!startsInDialog(event.target, rootRef.current?.closest('[role="dialog"]'))) return;
      if (isTypedChar(event)) track(event);
      else if (isFreshEnter(event)) onEnter(event);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [rootRef, openRef]);
}

/**
 * Egy javítható mező a pult ablakában (Karlo §2).
 *
 * MIÉRT KÜLÖN KOMPONENS. Három példánya van, és mindegyik ugyanazt a három
 * dolgot csinálja: címke, mező, és egy jelzés, ha az érték elmozdult az
 * eredetitől. Beágyazva ez háromszorosan ismételt feltételes ág lenne a
 * `TicketBody`-ban — pont az a fajta növekedés, ami ezt a fájlt eredetileg
 * kiszakította a `CheckIn.tsx`-ből.
 *
 * MIÉRT VAN „módosítva" JELZÉS. A pultnál a kezelő a gépeléssel egy MÁS ember
 * adatát is elronthatja (rossz sort olvasott be, vagy félreütött). A jelzés
 * nem díszítés: ez mondja meg egy pillantásra, hogy ezen a jegyen most
 * változtatás történik, mielőtt a gomb elmenti.
 */
interface EditableFieldProps {
  label: string;
  /** A mező alatti halvány megjegyzés — ma csak a cégnévnek van. */
  optional?: string;
  value: string;
  /** A jegyen ma szereplő érték, a „módosítva" jelzés összehasonlításához. */
  original: string;
  onChange: (value: string) => void;
  /** Névmező: nagy, talpas betűvel, ahogy a terv a nevet kiemeli. */
  display?: boolean;
}

function EditableField({
  label,
  optional,
  value,
  original,
  onChange,
  display = false,
}: EditableFieldProps): React.ReactElement {
  const { t } = useTranslation();
  const changed = fieldChanged(value, original);
  const className = [
    'scan-field',
    display ? 'scan-field--name' : '',
    changed ? 'scan-field--changed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <span className="scan-field__label">
        {label}
        {optional ? <span className="scan-field__hint"> {optional}</span> : null}
      </span>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // A mező feliratát az olvasó is használja: a `aria-label` nélkül a
        // próbák és a képernyőolvasó is csak egy névtelen dobozt látna.
        aria-label={label}
        /*
          A suffix MINDIG ott van, csak üres, ha nincs eltérés — és ez nem
          stílus. Ha a suffix az első leütéskor JELENNE MEG, az antd a mezőt
          új DOM-elemre cseréli, és a FÓKUSZ ELVÉSZ: a kezelő egy betűt ír be,
          a többi a semmibe megy (az antd maga is figyelmeztet erre). A
          próbában mérve: a javított mező a leütés után nem volt fókuszban, és
          a benne ütött Enter nem indított semmit (2026-09-18).
        */
        suffix={
          <span className="scan-field__suffix">
            {changed ? (
              t('checkin.scanEdited')
            ) : (
              <EditOutlined className="scan-field__pen" aria-hidden />
            )}
          </span>
        }
      />
    </div>
  );
}

export default ScanResultModal;
